/**
 * Content discovery.
 *
 * Generated sets are picked up by glob, so adding a season means dropping an
 * authored JSON into content/sets/ and rebuilding — no code change here, and
 * no import to remember.
 */

import type { TrackerSet } from '@/domain/content.types';

const modules = import.meta.glob<TrackerSet>('./generated/*.set.json', {
  eager: true,
  import: 'default',
});

/**
 * Seasonal checklists come first because `defaultSetId()` takes the head of
 * this list: a first-time visitor should land on the running event, not on
 * whichever collection happened to be edited last.
 */
const CATEGORY_RANK: Record<TrackerSet['category'], number> = {
  season: 0,
  event: 0,
  collection: 1,
  other: 2,
};

const sets: TrackerSet[] = Object.values(modules).sort((a, b) => {
  const byCategory = CATEGORY_RANK[a.category] - CATEGORY_RANK[b.category];
  if (byCategory !== 0) return byCategory;
  // Newest content first; ties fall back to title so ordering is stable.
  const byVersion = b.contentVersion.localeCompare(a.contentVersion);
  return byVersion !== 0 ? byVersion : a.title.en.localeCompare(b.title.en);
});

const byId = new Map(sets.map((s) => [s.id, s]));

export function listSets(): readonly TrackerSet[] {
  return sets;
}

export function getSet(id: string): TrackerSet | undefined {
  return byId.get(id);
}

export function defaultSetId(): string {
  const first = sets[0];
  if (!first) throw new Error('No content sets found. Run `npm run content:build`.');
  return first.id;
}

/**
 * Maps historical item ids onto current ones so progress survives an id change.
 * Built once per set; see `aliases` in the content schema.
 */
export function aliasMap(set: TrackerSet): ReadonlyMap<string, string> {
  const map = new Map<string, string>();
  for (const group of set.groups) {
    for (const item of group.items) {
      for (const alias of item.aliases ?? []) map.set(alias, item.id);
    }
  }
  return map;
}
